import DeviceProfileCreateUpdateModel from '@shared/models/deviceProfiles/DeviceProfileCreateUpdateModel';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import _ from 'lodash';

/**
 * Shared helpers for persisting macro favorites. Favorites are stored as `isFavorite` on each
 * macro inside a profile's configuration; there is no dedicated favorites endpoint, so saving
 * favorites means a full `updateProfile`. These helpers apply a `macroId -> isFavorite` map onto
 * a profile and build the API payload, keeping the favorite-save logic in one place.
 */

/**
 * Returns true if applying `favorites` to `profile` would change any macro's `isFavorite`.
 * Used to skip a no-op API write when the canonical profile already matches the panel state.
 */
export function favoritesDifferFromProfile(
    profile: UserDeviceProfileModel | null | undefined,
    favorites: Record<string, boolean>
): boolean {
    const macros = profile?.configuration?.macros;
    if (!macros) return false;
    return macros.some((m) => m.id != null && m.id in favorites && !!m.isFavorite !== favorites[m.id]);
}

/**
 * Returns a deep clone of `profile` with each macro's `isFavorite` set from `favorites` (by macro id).
 * Macros not present in the map are left untouched.
 */
export function applyFavoritesToProfile(
    profile: UserDeviceProfileModel,
    favorites: Record<string, boolean>
): UserDeviceProfileModel {
    const clone = _.cloneDeep(profile);
    clone.configuration.macros = (clone.configuration.macros ?? []).map((m) =>
        m.id != null && m.id in favorites ? { ...m, isFavorite: favorites[m.id] } : m
    );
    return clone;
}

/**
 * Builds the `updateProfile` payload for a profile with favorites applied. The canonical profile
 * (from the profiles store) is the base so concurrent edits to other fields are preserved.
 */
export function buildFavoritesUpdateModel(
    profile: UserDeviceProfileModel,
    favorites: Record<string, boolean>
): DeviceProfileCreateUpdateModel {
    const withFavorites = applyFavoritesToProfile(profile, favorites);
    return {
        name: withFavorites.name,
        description: withFavorites.description ?? '',
        profileType: withFavorites.configuration.profileType,
        configuration: withFavorites.configuration,
        defaultLocale: withFavorites.defaultLocale ?? '',
        originatedByProfileId: withFavorites.originatedByProfileId,
        requiresEmployeeAccess: withFavorites.requiresEmployeeAccess ?? false,
        requiresStudentAccess: withFavorites.requiresStudentAccess ?? false,
        requiresGenericLicense: withFavorites.requiresGenericLicense ?? false,
        requiresPlusAccess: withFavorites.requiresPlusAccess ?? false,
        platform: withFavorites.platform ?? 'generic',
        interests: withFavorites.interests ?? [],
        organizationId: withFavorites.organizationId ?? undefined,
    };
}
